import { CONSTANTS } from 'config/constants';
import { CustomerSetting } from 'models/customer-setting';
import { Department } from 'models/organization';
import { Steps } from 'models/workflow-step';
import { customerSettings } from 'utils/customerSettings';

/**
 * DD-3545: production value steps can be linked to workflow steps, so the technician who
 * did the workflow step is filled in on the production value report. Only for labs with both
 * Production Planning and the Production Value Report on.
 */
export const isWorkflowValueLinkEnabled = (settings: CustomerSetting[] = []) =>
    Boolean(customerSettings.getCustomerSetting(settings, CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING)) &&
    Boolean(customerSettings.getCustomerSetting(settings, CONSTANTS.SETTINGS_NAMES.PRODUCTION_VALUE_REPORT));

export type LinkableWorkflowStep = {
    _id: string;
    name: string;
    departmentId?: string;
    departmentName: string;
};

/** Workflow steps with their department, those in `departmentId` first, then by department. */
export const linkableWorkflowSteps = (
    steps: Steps[] = [],
    departments: Department[] = [],
    departmentId?: string,
): LinkableWorkflowStep[] => {
    const departmentByStepId = new Map<string, Department>();
    departments.forEach((department) =>
        department.workflowSteps?.forEach((workflowStep) =>
            departmentByStepId.set(String(workflowStep._id), department),
        ),
    );

    const options = steps
        .filter((step): step is Steps & { _id: string } => Boolean(step._id))
        .map((step) => {
            const department = departmentByStepId.get(step._id);
            return {
                _id: step._id,
                name: step.name,
                departmentId: department?._id,
                departmentName: department?.name ?? '',
            };
        });

    // Same department first, steps without a department last; a stable sort keeps the lab's own
    // workflow order inside each group.
    const rank = (option: LinkableWorkflowStep) => {
        if (departmentId && option.departmentId === departmentId) return 0;
        return option.departmentId ? 1 : 2;
    };
    return [...options].sort(
        (first, second) => rank(first) - rank(second) || first.departmentName.localeCompare(second.departmentName),
    );
};
